import { SceneService } from '../../services/SceneService';
import { LayoutSegmentFactory, Scene } from '../models/layout';
import { mapFitTypeToTemplateId } from '../models/layout/legacyLayoutMapping';

import type {
  FaceDetection,
  ISceneFramingService,
} from '../../services/framing/SmartFramingService';
import type { Element } from '../models/Element';
import type { CropTransformKeyframe } from '../models/layout';
import type { Timeline } from '../models/Timeline';
import type { SceneDetection } from '../types/sceneDetection';
import type { Command } from './Command';

/**
 * Parameters for the restored region
 */
export interface RestoredRegionParams {
  /** Elements containing the restored content */
  elements: Element[];
  /** Media start time of the restored region */
  mediaStartTime: number;
  /** Media end time of the restored region */
  mediaEndTime: number;
}

/**
 * Scene segment that overlaps with the restored region
 */
interface OverlappingDetection {
  detectionIndex: number;
  startTime: number;
  endTime: number;
}

/**
 * CreateScenesForRestoredRegionCommand
 *
 * Creates scenes for a restored portion of video content using scene detections.
 * Unlike CreateScenesByMediaTimeCommand which replaces all scenes, this command:
 * - Only creates scenes within the specified media time range
 * - Preserves existing scenes outside the restored region
 * - Attempts to merge with adjacent existing scenes when they belong to the same detection
 *
 * Designed to be used both:
 * - As a sub-command (called directly via execute(), not through executeCommand())
 * - Standalone via executeCommand() if needed
 *
 * When used as sub-command, shouldCreateUndoRedoEntry() returns false to prevent
 * duplicate undo entries.
 */
export class CreateScenesForRestoredRegionCommand implements Command {
  private sceneService = new SceneService();

  // State for undo
  private addedSceneIds: string[] = [];
  private modifiedSceneSnapshots: Map<string, Scene> = new Map();
  private removedSceneSnapshots: Map<string, Scene> = new Map();
  private executed = false;

  private static readonly PRECISION = 0.1;

  private readonly _shouldCreateUndoRedoEntry: boolean;

  constructor(
    private timeline: Timeline,
    private sceneDetections: SceneDetection[],
    private restoredRegion: RestoredRegionParams,
    private trackId: string,
    /**
     * When false, this command is being used as a sub-command and won't create
     * its own undo/redo entry. The parent command handles undo.
     */
    createUndoEntry: boolean = false,
    /** Optional crop to apply to all created scenes (fallback when no smart framing) */
    private crop?: CropTransformKeyframe['crop'],
    /** Optional framing service for calculating per-scene smart crops */
    private framingService?: ISceneFramingService,
    /** Face detections for smart framing; required along with framingService for auto-framing */
    private faceDetections?: FaceDetection[],
  ) {
    this._shouldCreateUndoRedoEntry = createUndoEntry;
  }

  getName(): string {
    return 'CreateScenesForRestoredRegionCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    if (this.executed) {
      return false;
    }

    const { elements, mediaStartTime, mediaEndTime } = this.restoredRegion;

    if (elements.length === 0) {
      return false;
    }

    // Step 1: Find scene detections that overlap with the restored region
    const overlappingDetections = this.findOverlappingDetections(
      mediaStartTime,
      mediaEndTime,
    );

    if (overlappingDetections.length === 0) {
      // No scene detections cover this region - nothing to do
      this.executed = true;
      return true;
    }

    // Step 2: Build element data with media times (needed for boundary lookup)
    const elementData = elements.map(element => ({
      element,
      mediaStartTime: this.sceneService.getElementMediaStartTime(element),
      mediaEndTime: this.sceneService.getElementMediaEndTime(element),
    }));

    // Step 3: Find existing scenes that overlap with our region boundaries
    // Find the element containing each boundary (restored region may span multiple elements)
    const startElementData =
      elementData.find(
        ed =>
          ed.mediaStartTime <= mediaStartTime &&
          ed.mediaEndTime > mediaStartTime,
      ) ?? elementData[0];

    const endElementData =
      elementData.find(
        ed =>
          ed.mediaStartTime < mediaEndTime && ed.mediaEndTime >= mediaEndTime,
      ) ?? elementData[elementData.length - 1];

    const timelineStartTime = this.sceneService.mediaTimeToTimelineTime(
      startElementData.element,
      mediaStartTime,
    );
    const timelineEndTime = this.sceneService.mediaTimeToTimelineTime(
      endElementData.element,
      mediaEndTime,
    );

    // Find scenes that contain or border our region boundaries.
    // At exact boundaries between contiguous scenes, findSceneAtTimelineTime
    // may return either neighbor. We probe both at and slightly outside
    // the boundary to capture left/right neighbors reliably.
    const EPSILON = 0.001;
    const sceneAtStart = this.sceneService.findSceneAtTimelineTime(
      this.timeline,
      this.trackId,
      timelineStartTime,
    );
    const sceneBeforeStart = this.sceneService.findSceneAtTimelineTime(
      this.timeline,
      this.trackId,
      timelineStartTime - EPSILON,
    );
    const sceneAtEnd = this.sceneService.findSceneAtTimelineTime(
      this.timeline,
      this.trackId,
      timelineEndTime,
    );
    const sceneAfterEnd = this.sceneService.findSceneAtTimelineTime(
      this.timeline,
      this.trackId,
      timelineEndTime + EPSILON,
    );

    // Step 3.5: Filter out detections already covered by existing boundary scenes.
    // A detection is "covered" if a boundary scene's origin detection matches it,
    // meaning the existing scene already represents that camera angle.
    // Additionally, expand boundary scenes to cover their origin detection's
    // portion within the restored region — this avoids creating a separate scene
    // that would carve the boundary scene unnecessarily.
    const uncoveredDetections = this.filterCoveredDetections(
      overlappingDetections,
      [sceneAtStart, sceneBeforeStart, sceneAtEnd, sceneAfterEnd],
      elementData,
    );

    if (uncoveredDetections.length === 0) {
      this.executed = true;
      return true;
    }

    // Step 4: Create scenes for each uncovered detection within the restored region
    const templateId = mapFitTypeToTemplateId('cover');
    const newScenes: Scene[] = [];

    for (let i = 0; i < uncoveredDetections.length; i++) {
      const detection = uncoveredDetections[i];

      // Clamp detection times to restored region bounds
      const clampedStart = Math.max(detection.startTime, mediaStartTime);
      const clampedEnd = Math.min(detection.endTime, mediaEndTime);

      // Create new scene for this detection
      const scene = this.createSceneForDetection(
        elementData,
        clampedStart,
        clampedEnd,
        templateId,
        this.crop,
      );

      if (scene) {
        newScenes.push(scene);
      }
    }

    // Step 5: Add all new scenes to the timeline, carving out space from existing scenes
    for (const scene of newScenes) {
      const result = this.sceneService.addScene(
        this.timeline,
        scene,
        'carve-out',
      );
      this.addedSceneIds.push(scene.id);

      if (result) {
        // Separate removed scenes from modified scenes for proper undo handling
        const removedSet = new Set(result.removedSceneIds);

        for (const adjusted of result.modifiedScenes) {
          if (removedSet.has(adjusted.sceneId)) {
            // Scene was removed - store for re-adding during undo
            if (!this.removedSceneSnapshots.has(adjusted.sceneId)) {
              this.removedSceneSnapshots.set(
                adjusted.sceneId,
                adjusted.originalSnapshot,
              );
            }
          } else {
            // Scene was modified - store for restoring during undo
            if (!this.modifiedSceneSnapshots.has(adjusted.sceneId)) {
              this.modifiedSceneSnapshots.set(
                adjusted.sceneId,
                adjusted.originalSnapshot,
              );
            }
          }
        }
        // Track continuation scenes from splits for removal during undo
        this.addedSceneIds.push(...result.addedSceneIds);
      }
    }

    this.executed = true;
    return true;
  }

  undo(): boolean {
    if (!this.executed) {
      return false;
    }

    // Remove added scenes
    for (const sceneId of this.addedSceneIds) {
      this.sceneService.removeScene(this.timeline, sceneId);
    }

    // Re-add removed scenes from their original snapshots
    for (const [, originalScene] of this.removedSceneSnapshots) {
      this.sceneService.addScene(this.timeline, originalScene, 'none');
    }

    // Restore modified scenes to their original state
    for (const [sceneId, originalScene] of this.modifiedSceneSnapshots) {
      this.sceneService.updateScene(this.timeline, sceneId, originalScene);
    }

    this.addedSceneIds = [];
    this.modifiedSceneSnapshots.clear();
    this.removedSceneSnapshots.clear();
    this.executed = false;

    return true;
  }

  redo(): boolean {
    if (this.executed) {
      return false;
    }
    return this.execute();
  }

  getDescription(): string {
    const { mediaStartTime, mediaEndTime } = this.restoredRegion;
    return `Create scenes for restored region [${mediaStartTime.toFixed(
      2,
    )}s - ${mediaEndTime.toFixed(2)}s]`;
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  /**
   * Find all scene detections that overlap with the restored region
   */
  private findOverlappingDetections(
    mediaStartTime: number,
    mediaEndTime: number,
  ): OverlappingDetection[] {
    const overlapping: OverlappingDetection[] = [];

    this.sceneDetections.forEach((detection, index) => {
      const detectionStart = detection.startTime;
      const detectionEnd = detection.startTime + detection.lengthSeconds;

      // Check if detection overlaps with restored region
      if (detectionEnd > mediaStartTime && detectionStart < mediaEndTime) {
        overlapping.push({
          detectionIndex: index,
          startTime: detectionStart,
          endTime: detectionEnd,
        });
      }
    });

    // Sort by start time
    return overlapping.sort((a, b) => a.startTime - b.startTime);
  }

  /**
   * Calculates the media time for an ElementAnchor
   */
  private getMediaTimeFromAnchor(
    element: Element,
    anchor: { relativeTime: number | null; end: boolean },
  ): number | null {
    const mediaStart = this.sceneService.getElementMediaStartTime(element);
    const mediaEnd = this.sceneService.getElementMediaEndTime(element);

    if (anchor.end) {
      // AnchorResolver ignores relativeTime when end:true — always resolves to element end
      return mediaEnd;
    } else {
      // relativeTime is measured from the start
      return mediaStart + (anchor.relativeTime ?? 0);
    }
  }

  /**
   * Creates a scene for a detection within the restored region
   */
  private createSceneForDetection(
    elementData: Array<{
      element: Element;
      mediaStartTime: number;
      mediaEndTime: number;
    }>,
    sceneStartTime: number,
    sceneEndTime: number,
    templateId: string,
    crop?: CropTransformKeyframe['crop'],
  ): Scene | null {
    // Find element containing the scene start
    const startElement = elementData.find(
      ed =>
        ed.mediaStartTime <= sceneStartTime && ed.mediaEndTime > sceneStartTime,
    );

    // Find element containing the scene end
    const endElement = elementData.find(
      ed => ed.mediaStartTime < sceneEndTime && ed.mediaEndTime >= sceneEndTime,
    );

    if (!startElement || !endElement) {
      return null;
    }

    const startAnchor = this.sceneService.createAnchorForMediaTime(
      startElement.element,
      sceneStartTime,
      startElement.mediaStartTime,
      startElement.mediaEndTime,
    );

    const endAnchor = this.sceneService.createAnchorForMediaTime(
      endElement.element,
      sceneEndTime,
      endElement.mediaStartTime,
      endElement.mediaEndTime,
    );

    if (!startAnchor || !endAnchor) {
      return null;
    }

    const layout = LayoutSegmentFactory.create(templateId, this.trackId, crop);

    const scene = new Scene({
      anchorStart: startAnchor,
      anchorEnd: endAnchor,
      layout,
    });

    // Apply smart framing if available
    if (
      this.shouldApplySmartFraming() &&
      this.framingService &&
      this.faceDetections?.length
    ) {
      const timelineStart = this.sceneService.mediaTimeToTimelineTime(
        startElement.element,
        sceneStartTime,
      );
      const timelineEnd = this.sceneService.mediaTimeToTimelineTime(
        startElement.element,
        sceneEndTime,
      );
      const timelineAspectRatio = this.timeline.width / this.timeline.height;
      const isNineBySixteen =
        Math.abs(timelineAspectRatio - 9 / 16) <
        CreateScenesForRestoredRegionCommand.PRECISION;
      this.framingService.autoFrameSingleScene(
        { scene, startTime: timelineStart, endTime: timelineEnd },
        this.faceDetections,
        isNineBySixteen ? undefined : timelineAspectRatio,
      );
    }

    return scene;
  }

  /**
   * Determines if smart framing should be applied based on timeline aspect ratio.
   * Smart framing is skipped for 16:9 aspect ratio since no cropping is needed.
   */
  private shouldApplySmartFraming(): boolean {
    const aspectRatio = this.timeline.width / this.timeline.height;
    const is16x9 = Math.abs(aspectRatio - 16 / 9) < 0.01;
    return !is16x9;
  }

  /**
   * Filters out detections that are already covered by existing boundary scenes.
   * A detection is "covered" when a boundary scene's origin detection matches it,
   * meaning the scene already represents that camera angle / scene detection.
   */
  private filterCoveredDetections(
    detections: OverlappingDetection[],
    boundarySceneCandidates: (Scene | null)[],
    elementData: Array<{
      element: Element;
      mediaStartTime: number;
      mediaEndTime: number;
    }>,
  ): OverlappingDetection[] {
    const boundaryScenes = new Set<Scene>();
    for (const s of boundarySceneCandidates) {
      if (s) boundaryScenes.add(s);
    }

    if (boundaryScenes.size === 0) {
      return detections;
    }

    // Compute origin detection for each boundary scene and expand the scene
    // to cover its origin detection's portion within the restored region.
    const coveredDetections = new Set<OverlappingDetection>();
    for (const scene of boundaryScenes) {
      const origin = this.getSceneOriginDetection(scene, detections);
      if (origin) {
        coveredDetections.add(origin);
        this.expandSceneToDetectionBounds(scene, origin, elementData);
      }
    }

    if (coveredDetections.size === 0) {
      return detections;
    }

    return detections.filter(d => !coveredDetections.has(d));
  }

  /**
   * Adjusts a boundary scene so its extent within the restored region aligns
   * with its origin detection's bounds within that region. This prevents the
   * scene from occupying space that belongs to other detections.
   *
   * - Left-neighbor (ends at/before region start): extend end into the region.
   * - Right-neighbor (starts at/after region end): extend start into the region.
   * - Overlapping/containing: adjust start or end within the region to match
   *   the detection's clamped bounds.
   */
  private expandSceneToDetectionBounds(
    scene: Scene,
    detection: OverlappingDetection,
    elementData: Array<{
      element: Element;
      mediaStartTime: number;
      mediaEndTime: number;
    }>,
  ): void {
    const { mediaStartTime, mediaEndTime } = this.restoredRegion;
    const P = CreateScenesForRestoredRegionCommand.PRECISION;

    const sceneStartMedia = this.resolveAnchorMediaTime(scene.anchorStart);
    const sceneEndMedia = this.resolveAnchorMediaTime(scene.anchorEnd);
    if (sceneStartMedia === null || sceneEndMedia === null) return;

    // Detection bounds clamped to the restored region
    const detRegionStart = Math.max(detection.startTime, mediaStartTime);
    const detRegionEnd = Math.min(detection.endTime, mediaEndTime);

    // Determine which boundary faces into the region and adjust it.

    // Scene: [====]
    // Region:       [============]
    // Scene's end is at/before region start → left neighbor, extend end
    if (sceneEndMedia <= mediaStartTime + P) {
      if (detRegionEnd > sceneEndMedia + P) {
        this.setSceneEnd(scene, detRegionEnd, elementData);
      }
      return;
    }

    // Region: [============]
    // Scene:                [====]
    // Scene's start is at/after region end → right neighbor, extend start
    if (sceneStartMedia >= mediaEndTime - P) {
      if (detRegionStart < sceneStartMedia - P) {
        this.setSceneStart(scene, detRegionStart, elementData);
      }
      return;
    }

    // Scene overlaps/contains the region. Adjust whichever boundary is
    // within the restored region to align with the detection bounds.

    // Region: [============]
    // Scene:       [==============]
    // If scene start is within the region, move it to detection's region start
    if (
      sceneStartMedia >= mediaStartTime - P &&
      sceneStartMedia < mediaEndTime
    ) {
      if (Math.abs(sceneStartMedia - detRegionStart) > P) {
        this.setSceneStart(scene, detRegionStart, elementData);
      }
    }

    // Region:      [============]
    // Scene:  [===========]
    // If scene end is within the region, move it to detection's region end
    if (sceneEndMedia > mediaStartTime && sceneEndMedia <= mediaEndTime + P) {
      if (Math.abs(sceneEndMedia - detRegionEnd) > P) {
        this.setSceneEnd(scene, detRegionEnd, elementData);
      }
    }
  }

  /**
   * Sets a scene's end anchor to a new media time.
   */
  private setSceneEnd(
    scene: Scene,
    targetMediaTime: number,
    elementData: Array<{
      element: Element;
      mediaStartTime: number;
      mediaEndTime: number;
    }>,
  ): void {
    // Snapshot for undo before modification
    if (!this.modifiedSceneSnapshots.has(scene.id)) {
      this.modifiedSceneSnapshots.set(scene.id, Scene.fromJSON(scene.toJSON()));
    }

    const endElement =
      elementData.find(
        ed =>
          ed.mediaStartTime < targetMediaTime &&
          ed.mediaEndTime >= targetMediaTime,
      ) ?? elementData[elementData.length - 1];

    const newAnchor = this.sceneService.createAnchorForMediaTime(
      endElement.element,
      targetMediaTime,
      endElement.mediaStartTime,
      endElement.mediaEndTime,
    );
    if (newAnchor) {
      scene.setAnchorEnd(newAnchor);
    }
  }

  /**
   * Sets a scene's start anchor to a new media time.
   */
  private setSceneStart(
    scene: Scene,
    targetMediaTime: number,
    elementData: Array<{
      element: Element;
      mediaStartTime: number;
      mediaEndTime: number;
    }>,
  ): void {
    // Snapshot for undo before modification
    if (!this.modifiedSceneSnapshots.has(scene.id)) {
      this.modifiedSceneSnapshots.set(scene.id, Scene.fromJSON(scene.toJSON()));
    }

    const startElement =
      elementData.find(
        ed =>
          ed.mediaStartTime <= targetMediaTime &&
          ed.mediaEndTime > targetMediaTime,
      ) ?? elementData[0];

    const newAnchor = this.sceneService.createAnchorForMediaTime(
      startElement.element,
      targetMediaTime,
      startElement.mediaStartTime,
      startElement.mediaEndTime,
    );
    if (newAnchor) {
      scene.setAnchorStart(newAnchor);
    }
  }

  /**
   * Determines which overlapping detection a scene "originated from" by resolving
   * its anchor media times and checking which detection contains them.
   *
   * Primary heuristic: use scene start media time.
   * Fallback: if the scene's start was shifted (coincides with restored region
   * boundary, e.g. after adjustScenesAfterExtendStart), use scene end instead.
   */
  private getSceneOriginDetection(
    scene: Scene,
    detections: OverlappingDetection[],
  ): OverlappingDetection | null {
    const startMediaTime = this.resolveAnchorMediaTime(scene.anchorStart);
    const endMediaTime = this.resolveAnchorMediaTime(scene.anchorEnd);
    const { mediaStartTime, mediaEndTime } = this.restoredRegion;
    const P = CreateScenesForRestoredRegionCommand.PRECISION;

    // Detect if start was shifted by a prior adjustScenes* call
    const startWasShifted =
      startMediaTime !== null &&
      (Math.abs(startMediaTime - mediaStartTime) < P ||
        Math.abs(startMediaTime - mediaEndTime) < P);

    // Primary: use scene start
    if (!startWasShifted && startMediaTime !== null) {
      // Prefer the detection whose interior contains the start time.
      // When start is at a boundary between two detections, prefer the one
      // that starts there (the scene likely belongs to the detection beginning
      // at that point, not the one ending there).
      const origin =
        detections.find(
          d =>
            startMediaTime >= d.startTime - P && startMediaTime < d.endTime - P,
        ) ??
        detections.find(
          d =>
            startMediaTime >= d.startTime - P && startMediaTime < d.endTime + P,
        );
      if (origin) return origin;
    }

    // Fallback: use scene end. A scene ending at (or just past) a detection's
    // start belongs to the detection before it, not that one — otherwise a
    // neighbour ending exactly on a boundary (the norm for speaker scenes)
    // swallows the next detection's restored content.
    if (endMediaTime !== null) {
      const origin = detections.find(
        d => endMediaTime > d.startTime + P && endMediaTime <= d.endTime + P,
      );
      if (origin) return origin;
    }

    return null;
  }

  /**
   * Resolves an anchor to its media time by looking up the element from the timeline.
   * Delegates to getMediaTimeFromAnchor after resolving the element.
   */
  private resolveAnchorMediaTime(
    anchor: Scene['anchorStart'] | Scene['anchorEnd'],
  ): number | null {
    if (typeof anchor !== 'object' || !('elementId' in anchor)) return null;

    const { elementId, relativeTime, end } = anchor as {
      elementId: string;
      relativeTime: number | null;
      end: boolean;
    };

    const element = this.timeline.getElement(elementId);
    if (!element) return null;

    return this.getMediaTimeFromAnchor(element, { relativeTime, end });
  }
}
